import { buildDeliveryNoteUnitOptions } from "./deliveryNoteUnits";

const text = (value) => typeof value === "string" ? value.trim() : "";
const normalized = (value) => text(value).normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase().replace(/\s+/g, " ").replace(/\.$/, "");
const groups = [
  ["kg", "kgs", "kilo", "kilos", "kilogram", "kilograms", "kilogramo", "kilogramos", "quilogram", "quilograms"],
  ["g", "gr", "grs", "gram", "grams", "gramo", "gramos"],
  ["l", "lt", "liter", "liters", "litre", "litres", "litro", "litros"],
  ["ml", "milliliter", "milliliters", "millilitre", "millilitres", "mililitro", "mililitros"],
  ["cl", "centiliter", "centiliters", "centilitre", "centilitres", "centilitro", "centilitros"],
  ["unit", "units", "ud", "uds", "unidad", "unidades", "unitat", "unitats"],
  ["box", "boxes", "caja", "cajas", "caixa", "caixes"],
  ["pack", "packs"],
  ["bottle", "bottles", "botella", "botellas", "ampolla", "ampolles"],
  ["bag", "bags", "bolsa", "bolsas", "bossa", "bosses"],
  ["tray", "trays", "bandeja", "bandejas", "safata", "safates"],
  ["bundle", "bundles", "manojo", "manojos", "mj", "manat", "manats"],
];
const aliases = new Map(groups.flatMap(([canonical, ...names]) => [canonical, ...names].map((name) => [name, canonical])));
const unitKey = (value) => aliases.get(normalized(value)) || normalized(value);
const explicitFields = ["UNIT", "UOM", "UNIT_OF_MEASURE", "UNIT_OF_MEASUREMENT", "MEASUREMENT_UNIT"];
const values = (value) => (Array.isArray(value) ? value : [value]).map(text).filter(Boolean);
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

// Existing imports retain bare, typed Textract fields. OTHER is deliberately a
// small whitelist: descriptions such as "Caja de 30 unidades" are not a unit column.
export function readOcrUnitEvidence(line = {}) {
  if (line.unit_label_needs_review === true) return { unit_label: text(line.unit_label), unit_label_needs_review: true };
  if (hasOwn(line, "unit_label")) return { unit_label: text(line.unit_label) };
  const fields = line.textract_fields || {};
  const products = ["ITEM", "DESCRIPTION", "PRODUCT", "PRODUCT_NAME", "NAME"].flatMap((field) => values(fields[field]));
  const labels = [...explicitFields.flatMap((field) => values(fields[field])),
    ...values(fields.OTHER).filter((label) => aliases.has(normalized(label)) &&
      products.some((product) => /[a-z]/.test(normalized(product)) && normalized(product) !== normalized(label)))];
  if (!labels.length) return {};
  if (new Set(labels.map(unitKey)).size > 1) return { unit_label: "", unit_label_needs_review: true };
  return { unit_label: labels[0] };
}

export const hasOcrAiUnitProposal = (line) => hasOwn(line, "_aiProposedUnitLabel") || hasOwn(line, "_aiProposedUnitLevel");

function optionNames(item, option) {
  const { level, unitId } = option;
  const unit = item[level];
  const nested = unit && (unitId == null || unit.id == null || String(unit.id) === String(unitId)) ? unit : {};
  return [option.label, nested.name, nested.code, ...Object.values(nested.name_translations || {}),
    item[`item_${level}_name`], item[`${level}_name`], ...Object.values(item[`${level}_name_translations`] || {}),
    ...(level === "unit" ? [item.inventory_unit_name, ...Object.values(item.inventory_unit_name_translations || {})] : []),
  ].map(unitKey).filter(Boolean);
}

// Only choose an existing level. Document quantity/price and pack factors are never changed.
export function matchOcrUnitLevel(item, line = {}) {
  const options = buildDeliveryNoteUnitOptions(item);
  const evidence = readOcrUnitEvidence(line);
  if (evidence.unit_label_needs_review) return "";
  if (!evidence.unit_label) return options.length === 1 ? options[0].level : "";
  const key = unitKey(evidence.unit_label);
  const matches = options.filter((option) => optionNames(item, option).includes(key));
  return matches.length === 1 ? matches[0].level : "";
}

const identity = (line) => {
  const description = normalized(line.description), code = text(line.supplier_code).toUpperCase();
  if (!description && !code) return "";
  // Reuse source evidence only for an unchanged and unique document row, not its array position.
  return JSON.stringify([description, code, String(line.quantity ?? ""), String(line.unit_price ?? "")]);
};

export function restoreOcrUnitEvidence(draftLines, originalLines, { aiApplied = false } = {}) {
  if (aiApplied) return draftLines;
  const originals = new Map(), counts = new Map();
  for (const line of originalLines) {
    const key = identity(line);
    if (key) originals.set(key, [...(originals.get(key) || []), line]);
  }
  for (const line of draftLines) {
    const key = identity(line);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return draftLines.map((line) => {
    if (hasOwn(line, "unit_label") || hasOwn(line, "unit_label_needs_review") || hasOcrAiUnitProposal(line)) return line;
    const key = identity(line), matches = originals.get(key);
    if (!key || counts.get(key) !== 1 || matches?.length !== 1) return line;
    const evidence = readOcrUnitEvidence(matches[0]);
    return Object.keys(evidence).length ? { ...line, ...evidence } : line;
  });
}
